import { useCallback, useRef } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { trackEvent } from '@/src/renderer/lib/analytics'
import { ANALYTICS_EVENTS } from '@/src/renderer/lib/analytics/events'
import type { Task } from '@/src/renderer/lib/db/models'
import { useIntroNote } from '@/src/renderer/lib/intro'

/** Woran ein Fokus geendet hat — für die Auswertung (`focus_ended`). */
export type FocusEndReason = 'pause' | 'completed' | 'switched' | 'suspended' | 'crash'

/**
 * **Play und Pause** — die eine Stelle, die einen Fokus beginnt und beendet.
 *
 * Sie tut zweierlei, und in dieser Reihenfolge: Erst wird **geschrieben**
 * (`startFocus`/`endFocus` in der Datenbank — dort steht die Wahrheit, ADR
 * 0025), dann werden die **Fenster** geordnet (Fokus-Fenster auf, Hauptfenster tritt
 * zurück — und zurück). Bräche der zweite Schritt, stünde die Zeit trotzdem
 * richtig; andersherum liefe ein Fenster ohne Zeit.
 *
 * Im Web gibt es den zweiten Schritt nicht: Dort deckt eine Fläche die App zu
 * (3d), und `window.api` ist gar nicht da.
 */
export function useFocus() {
  const source = useDataSource()
  const running = source.useRunningFocus()
  const noteIntro = useIntroNote()
  // Nur für die Länge in der Auswertung — die Wahrheit steht in der Datenbank.
  const startedAt = useRef<Date | null>(null)

  /** Sekunden seit `from`, für die Auswertung. */
  const secondsSince = (from: Date | null | undefined, until: Date) =>
    from ? Math.max(0, Math.round((until.getTime() - from.getTime()) / 1000)) : null

  const start = useCallback(async (task: Pick<Task, 'id' | 'title'>) => {
    // Play auf einer anderen Aufgabe bucht den laufenden Abschnitt — in der
    // Auswertung ist das ein Ende mit eigenem Grund, kein stilles Verschwinden.
    if (running && running.taskId !== task.id) {
      trackEvent(ANALYTICS_EVENTS.FOCUS_ENDED, {
        reason: 'switched' satisfies FocusEndReason,
        seconds: secondsSince(running.startTime, new Date()),
      })
    }

    const entry = await source.startFocus(task.id)
    startedAt.current = entry.startTime
    trackEvent(ANALYTICS_EVENTS.FOCUS_STARTED)
    noteIntro('focus')
    await window.api?.invoke('focus:started', { title: task.title })
  }, [source, running, noteIntro])

  const end = useCallback(async (options?: { at?: Date; reason?: FocusEndReason }) => {
    const at = options?.at ?? new Date()
    await source.endFocus(options?.at)
    trackEvent(ANALYTICS_EVENTS.FOCUS_ENDED, {
      reason: options?.reason ?? 'pause',
      // Nur die Länge, kein Titel und keine Kennung.
      seconds: secondsSince(startedAt.current ?? running?.startTime, at),
    })
    startedAt.current = null
    await window.api?.invoke('focus:ended')
  }, [source, running])

  return { startFocus: start, endFocus: end }
}

/**
 * **Der Play-Knopf einer Aufgabenzeile** — fertig zum Durchreichen als `onPlay`.
 *
 * Holt sich die Aufgabe selbst aus der Datenquelle, statt sie aus der Liste der
 * jeweiligen Seite zu fischen: Sonst stünde dieselbe Suche in fünf Seiten, jede
 * mit ihrem eigenen Namen für „die Aufgaben hier" — und eine davon griffe
 * irgendwann daneben, ohne dass es auffiele (der Klick täte dann einfach nichts).
 *
 * @example
 * const handlePlay = usePlayTask()
 * <TaskBoard … onPlay={handlePlay} />
 */
export function usePlayTask(): (taskId: string) => void {
  const source = useDataSource()
  const { startFocus } = useFocus()

  return useCallback((taskId: string) => {
    void (async () => {
      const task = await source.getTask(taskId)
      if (task) await startFocus(task)
    })()
  }, [source, startFocus])
}
